/* ==========================================================================
   Dialog Focus Management (WCAG 2.4.3 Focus Order)

  This script patches the existing slideout/modal system in theme.js to:
   1. Move focus into dialogs when they open
   2. Trap focus within the dialog while open
   3. Restore focus to the trigger element on close
   4. Set aria-hidden on background content while dialog is open
   ========================================================================== */

(function () {
  'use strict';

  var FOCUSABLE_SELECTORS = [
    'a[href]:not([disabled]):not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([disabled]):not([tabindex="-1"])'
  ].join(', ');

  var activeDialog = null;
  var lastOpenerElement = null;

  /**
   * Gets all focusable elements within a container.
   */
  function getFocusableElements(container) {
    return Array.prototype.slice.call(
      container.querySelectorAll(FOCUSABLE_SELECTORS)
    ).filter(function (el) {
      return el.offsetParent !== null; // only visible elements
    });
  }

  /**
   * Traps focus within a dialog container.
   * When Tab reaches the last element, it wraps to the first.
   * When Shift+Tab reaches the first element, it wraps to the last.
   */
  function handleFocusTrap(event) {
    if (event.key !== 'Tab' || !activeDialog) return;

    var focusableElements = getFocusableElements(activeDialog);
    if (focusableElements.length === 0) return;

    var firstElement = focusableElements[0];
    var lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift+Tab: if on first element, wrap to last
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: if on last element, wrap to first
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Sets aria-hidden on background content when a dialog opens.
   */
  function hideBackgroundContent(hide) {
    var siteWrap = document.querySelector('.site-wrap');
    if (siteWrap) {
      if (hide) {
        siteWrap.setAttribute('aria-hidden', 'true');
      } else {
        siteWrap.removeAttribute('aria-hidden');
      }
    }
  }

  /**
   * Moves focus into a dialog and activates focus trapping.
   * Captures document.activeElement (the element the user actually interacted
   * with, e.g. "Add to Cart") rather than the selector-based opener (which
   * points to the header cart icon). This ensures focus returns to the
   * correct button when the dialog closes.
   */
  function activateDialog(dialogEl, openerEl) {
    activeDialog = dialogEl;
    // Prefer the currently focused element (what the user actually clicked)
    // over the slideout system's "opener" (the header cart icon).
    lastOpenerElement = document.activeElement || openerEl;

    // Hide background content from screen readers
    hideBackgroundContent(true);

    // Add focus trap listener
    document.addEventListener('keydown', handleFocusTrap);

    // Move focus into the dialog after a short delay
    // (allows the slideout/modal animation to begin)
    setTimeout(function () {
      var focusableElements = getFocusableElements(dialogEl);
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      } else {
        // If no focusable element found, focus the dialog container itself
        dialogEl.setAttribute('tabindex', '-1');
        dialogEl.focus();
      }
    }, 100);
  }

  /**
   * Deactivates focus trapping and restores focus to the opener.
   * Uses setTimeout to ensure this runs after theme.js's own
   * openerEl.focus() call (which targets the header cart icon),
   * so we correctly override focus back to the "Add to Cart" button.
   */
  function deactivateDialog() {
    // Remove focus trap listener
    document.removeEventListener('keydown', handleFocusTrap);

    // Unhide background content
    hideBackgroundContent(false);

    // Restore focus to the element that opened the dialog
    var elementToRestore = lastOpenerElement;
    if (elementToRestore) {
      setTimeout(function () {
        elementToRestore.focus();
      }, 0);
    }

    activeDialog = null;
    lastOpenerElement = null;
  }

  // -----------------------------------------------------------------------
  // Observe slideout (drawer) open/close via class changes
  // -----------------------------------------------------------------------

  /**
   * Uses MutationObserver to detect when slideout drawers and modals
   * are opened or closed, then manages focus accordingly.
   */
  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') return;

      var target = mutation.target;

      // --- Slideout drawers ---
      if (target.classList.contains('slideout')) {
        if (target.classList.contains('slideout--active') && !target._a11yActivated) {
          // Slideout just opened
          target._a11yActivated = true;
          var slideoutName = target.getAttribute('data-wau-slideout');
          var opener = document.querySelector(
            '.js-slideout-open[data-wau-slideout-target="' + slideoutName + '"]'
          );
          activateDialog(target, opener);
        } else if (!target.classList.contains('slideout--active') && target._a11yActivated) {
          // Slideout just closed
          target._a11yActivated = false;
          deactivateDialog();
        }
      }

      // --- Modals ---
      if (target.classList.contains('modal__container')) {
        if (target.classList.contains('modal--active') && !target._a11yActivated) {
          // Modal just opened
          target._a11yActivated = true;
          var modalName = target.getAttribute('data-wau-modal');
          var opener = document.querySelector(
            '.js-modal-open[data-wau-modal-target="' + modalName + '"]'
          );
          activateDialog(target, opener);
        } else if (!target.classList.contains('modal--active') && target._a11yActivated) {
          // Modal just closed
          target._a11yActivated = false;
          deactivateDialog();
        }
      }
    });
  });

  // Start observing once DOM is ready
  function init() {
    // Observe all slideout drawers
    var slideouts = document.querySelectorAll('.slideout[data-wau-slideout]');
    slideouts.forEach(function (el) {
      observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    });

    // Observe all modal containers
    var modals = document.querySelectorAll('.modal__container[data-wau-modal]');
    modals.forEach(function (el) {
      observer.observe(el, { attributes: true, attributeFilter: ['class'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();